import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, Euro, Calendar, AlertTriangle, CheckCircle2, Clock, Search,
  Loader2, Receipt, BadgeEuro, ArrowRight, TrendingUp, FilePenLine,
} from "lucide-react";
import { toast } from "sonner";

function fmt(c) {
  return ((c || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
}

const STATUS_META = {
  to_invoice: { label: "À facturer", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: FilePenLine },
  invoiced: { label: "Facturée", cls: "bg-blue-100 text-blue-800 border-blue-200", Icon: Receipt },
  paid: { label: "Payée", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: CheckCircle2 },
  overdue: { label: "En retard", cls: "bg-red-100 text-red-800 border-red-200", Icon: AlertTriangle },
};

export default function BillingProAdmin() {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);    // order being edited (modal)
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (q) params.set("q", q);
      const [oRes, sRes] = await Promise.all([
        api.get(`/jardins-ole/admin/crm/billing-pro/orders?${params.toString()}`),
        api.get("/jardins-ole/admin/crm/billing-pro/summary"),
      ]);
      setOrders(oRes.data || []);
      setSummary(sRes.data || null);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [statusFilter, q]);

  const counts = useMemo(() => {
    const c = { all: orders.length, to_invoice: 0, invoiced: 0, paid: 0, overdue: 0 };
    for (const o of orders) c[o.bill_status] = (c[o.bill_status] || 0) + 1;
    return c;
  }, [orders]);

  const openEdit = (o) => setEditing({
    ...o,
    _new_paid_at: o.paid_at || "",
    _new_invoice_status: o.invoice_status || "to_emit",
    _new_invoice_number: o.invoice_number || "",
    _new_payment_method_used: o.payment_method_used || "",
    _new_payment_notes: o.payment_notes || "",
  });

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        invoice_status: editing._new_invoice_status,
        invoice_number: editing._new_invoice_number,
        paid_at: editing._new_paid_at,
        payment_method_used: editing._new_payment_method_used,
        payment_notes: editing._new_payment_notes,
      };
      await api.patch(`/jardins-ole/admin/crm/billing-pro/orders/${editing.id}`, payload);
      toast.success("Commande mise à jour");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const markInvoiced = async (o) => {
    try {
      await api.patch(`/jardins-ole/admin/crm/billing-pro/orders/${o.id}`, { invoice_status: "emitted" });
      toast.success("Marquée facturée");
      load();
    } catch { toast.error("Erreur"); }
  };
  const markPaid = async (o) => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      await api.patch(`/jardins-ole/admin/crm/billing-pro/orders/${o.id}`, { paid_at: today });
      toast.success("Marquée payée");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="billing-pro-admin">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BadgeEuro className="w-6 h-6" /> Facturation pro &amp; encours
        </h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          Suivi des commandes pros à crédit (paiement à terme). Pointez les factures émises et les paiements reçus.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="flex items-center justify-between text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            <span>À encaisser</span><Euro className="w-3.5 h-3.5" />
          </div>
          <div className="text-3xl font-bold mt-1 text-[color:var(--brand-navy)]" data-testid="summary-to-collect">
            {fmt(summary?.sum_to_collect_cents)}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            <span>Échéance 7 j</span><Clock className="w-3.5 h-3.5" />
          </div>
          <div className="text-3xl font-bold mt-1 text-amber-700" data-testid="summary-due-week">
            {fmt(summary?.sum_due_this_week_cents)}
          </div>
        </Card>
        <Card className={`p-4 ${(summary?.sum_overdue_cents || 0) > 0 ? "border-red-300 bg-red-50" : ""}`}>
          <div className="flex items-center justify-between text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            <span>En retard</span><AlertTriangle className="w-3.5 h-3.5" />
          </div>
          <div className="text-3xl font-bold mt-1 text-red-700" data-testid="summary-overdue">
            {fmt(summary?.sum_overdue_cents)}
          </div>
          <div className="text-xs text-red-600 mt-0.5">{summary?.count_overdue || 0} commande(s)</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
            <span>Payées ce mois</span><TrendingUp className="w-3.5 h-3.5" />
          </div>
          <div className="text-3xl font-bold mt-1 text-emerald-700" data-testid="summary-paid-month">
            {fmt(summary?.cur_month_paid_cents)}
          </div>
        </Card>
      </div>

      {/* Reminder banner */}
      {(summary?.count_to_invoice || 0) > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm flex items-center gap-3" data-testid="reminder-banner">
          <FileText className="w-5 h-5 text-amber-700 shrink-0" />
          <div className="flex-1">
            <strong>{summary.count_to_invoice}</strong> commande(s) pro <strong>à facturer</strong>. Pensez à émettre les factures pour ne pas manquer les délais légaux.
          </div>
          <Button size="sm" variant="outline" onClick={() => setStatusFilter("to_invoice")}>Voir</Button>
        </div>
      )}

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher par n° commande, email, n° TVA..."
              className="pl-9"
              data-testid="billing-search-input"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {[
              ["", "Toutes"],
              ["to_invoice", "À facturer"],
              ["invoiced", "Facturées"],
              ["overdue", "En retard"],
              ["paid", "Payées"],
            ].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setStatusFilter(v)}
                className={`px-3 h-10 rounded-md text-xs font-medium border whitespace-nowrap ${
                  statusFilter === v
                    ? "bg-[color:var(--brand-sky)] text-white border-[color:var(--brand-sky)]"
                    : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
                }`}
                data-testid={`billing-filter-${v || "all"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Orders table */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
        </div>
      ) : orders.length === 0 ? (
        <Card className="p-12 text-center">
          <BadgeEuro className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-3" />
          <div className="text-[hsl(var(--muted-foreground))]">Aucune commande pro à crédit pour ce filtre.</div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))]/40 text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                <tr>
                  <th className="px-4 py-3 text-left">N° / Client</th>
                  <th className="px-4 py-3 text-left">Date</th>
                  <th className="px-4 py-3 text-left">Échéance</th>
                  <th className="px-4 py-3 text-right">Montant TTC</th>
                  <th className="px-4 py-3 text-left">Statut</th>
                  <th className="px-4 py-3 text-right w-48">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const meta = STATUS_META[o.bill_status] || STATUS_META.to_invoice;
                  const StatusIcon = meta.Icon;
                  const days = daysUntil(o.due_date);
                  return (
                    <tr key={o.id} className="border-t hover:bg-[hsl(var(--muted))]/20" data-testid={`billing-row-${o.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs font-semibold">{o.number}</div>
                        <Link to={`/admin/clients/${o.crm_client_id}`} className="text-xs text-[color:var(--brand-navy)] hover:underline">
                          {o.client_label}
                        </Link>
                        {o.invoice_number && (
                          <div className="text-[10px] text-[hsl(var(--muted-foreground))]">Facture {o.invoice_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">{fmtDate(o.created_at)}</td>
                      <td className="px-4 py-3 text-xs">
                        <div className="font-medium">{fmtDate(o.due_date)}</div>
                        {o.bill_status !== "paid" && days !== null && (
                          <div className={`text-[10px] ${days < 0 ? "text-red-600 font-bold" : days <= 7 ? "text-amber-700" : "text-[hsl(var(--muted-foreground))]"}`}>
                            {days < 0 ? `Retard ${Math.abs(days)} j` : days === 0 ? "Aujourd'hui" : `Dans ${days} j`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-bold text-[color:var(--brand-navy)]">{fmt(o.total_cents)}</div>
                        <div className="text-[10px] text-[hsl(var(--muted-foreground))]">HT {fmt(o.ht_cents)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${meta.cls}`}>
                          <StatusIcon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {o.bill_status === "to_invoice" && (
                            <Button size="sm" variant="outline" onClick={() => markInvoiced(o)} data-testid={`mark-invoiced-${o.id}`}>
                              <Receipt className="w-3.5 h-3.5 mr-1" />Facturée
                            </Button>
                          )}
                          {o.bill_status !== "paid" && (
                            <Button size="sm" variant="outline" onClick={() => markPaid(o)} data-testid={`mark-paid-${o.id}`}
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Payée
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-billing-${o.id}`}>
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="billing-edit-modal">
          <DialogHeader>
            <DialogTitle>
              {editing?.number} · {editing?.client_label}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <div className="bg-[hsl(var(--muted))]/30 rounded-md p-3 text-sm">
                <div className="flex justify-between"><span>HT</span><span className="tabular-nums">{fmt(editing.ht_cents)}</span></div>
                <div className="flex justify-between"><span>TVA 20 %</span><span className="tabular-nums">{fmt(editing.tax_cents)}</span></div>
                <div className="flex justify-between font-bold pt-1 border-t border-[hsl(var(--border))]">
                  <span>Total TTC</span><span className="tabular-nums">{fmt(editing.total_cents)}</span>
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                  Conditions : <strong>{editing.payment_terms || "—"}</strong> · Échéance <strong>{fmtDate(editing.due_date)}</strong>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Statut facture</Label>
                  <select
                    value={editing._new_invoice_status}
                    onChange={(e) => setEditing({ ...editing, _new_invoice_status: e.target.value })}
                    className="w-full border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white mt-1"
                    data-testid="edit-invoice-status"
                  >
                    <option value="to_emit">À émettre</option>
                    <option value="emitted">Émise</option>
                  </select>
                </div>
                <div>
                  <Label>N° de facture</Label>
                  <Input
                    value={editing._new_invoice_number}
                    onChange={(e) => setEditing({ ...editing, _new_invoice_number: e.target.value })}
                    placeholder="ex: F-2026-0042"
                    data-testid="edit-invoice-number"
                  />
                </div>
                <div>
                  <Label>Date de paiement reçu</Label>
                  <Input
                    type="date"
                    value={editing._new_paid_at?.slice(0, 10) || ""}
                    onChange={(e) => setEditing({ ...editing, _new_paid_at: e.target.value })}
                    data-testid="edit-paid-at"
                  />
                  <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Laissez vide si pas encore payée.</div>
                </div>
                <div>
                  <Label>Mode de règlement</Label>
                  <select
                    value={editing._new_payment_method_used}
                    onChange={(e) => setEditing({ ...editing, _new_payment_method_used: e.target.value })}
                    className="w-full border border-[hsl(var(--border))] rounded-md px-3 h-10 text-sm bg-white mt-1"
                    data-testid="edit-payment-method"
                  >
                    <option value="">—</option>
                    <option value="transfer">Virement</option>
                    <option value="check">Chèque</option>
                    <option value="card">CB</option>
                    <option value="cash">Espèces</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Notes paiement</Label>
                <Textarea
                  rows={3}
                  value={editing._new_payment_notes}
                  onChange={(e) => setEditing({ ...editing, _new_payment_notes: e.target.value })}
                  placeholder="Référence virement, n° chèque, remarques..."
                  data-testid="edit-payment-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Annuler</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-billing-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
